import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faLinkedinIn, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import styled, { keyframes } from 'styled-components';
import siemensLogo from '../../../assets/images/siemens.png';

/* =========================
   ANIMATIONS
   ========================= */

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 10px rgba(0,150,255,0.3), 0 0 20px rgba(0,150,255,0.2); }
  50% { box-shadow: 0 0 15px rgba(0,150,255,0.5), 0 0 30px rgba(0,150,255,0.4); }
  100% { box-shadow: 0 0 10px rgba(0,150,255,0.3), 0 0 20px rgba(0,150,255,0.2); }
`;

const electricBorder = keyframes`
  0% {
    background-position: 0% 50%;
    opacity: 0.9;
  }
  50% {
    background-position: 100% 50%;
    opacity: 1;
  }
  100% {
    background-position: 0% 50%;
    opacity: 0.9;
  }
`;

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

/* =========================
   REVEAL ON SCROLL (No GSAP)
   Uses IntersectionObserver + CSS transitions
   ========================= */

const RevealWrapper = styled.div`
  opacity: ${({ $visible, $animateOpacity, $initialOpacity }) =>
    $animateOpacity ? ($visible ? 1 : $initialOpacity) : 1};

  transform: ${({ $visible, $direction, $distance, $reverse, $scale }) => {
    const offset = $reverse ? -$distance : $distance;
    if ($visible) return 'translate3d(0,0,0) scale(1)';
    if ($direction === 'horizontal') {
      return `translate3d(${offset}px, 0, 0) scale(${$scale})`;
    }
    return `translate3d(0, ${offset}px, 0) scale(${$scale})`;
  }};

  transition:
    opacity ${({ $duration }) => $duration}s ${({ $ease }) => $ease},
    transform ${({ $duration }) => $duration}s ${({ $ease }) => $ease};
`;

const RevealOnScroll = ({
  children,
  distance = 100,
  direction = 'vertical',      // "vertical" | "horizontal"
  reverse = false,
  duration = 0.8,
  ease = 'power3.out',
  initialOpacity = 0,
  animateOpacity = true,
  scale = 1,
  threshold = 0.1,
  delay = 0,
  onComplete
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  // Map GSAP-style ease name to CSS ease
  const cssEase =
    ease === 'power3.out' ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'ease-out';

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const start = () => {
            setVisible(true);
            if (onComplete) onComplete();
          };

          if (delay && delay > 0) {
            setTimeout(start, delay * 1000);
          } else {
            start();
          }

          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, delay, onComplete]);

  return (
    <RevealWrapper
      ref={ref}
      $visible={visible}
      $distance={distance}
      $direction={direction}
      $reverse={reverse}
      $duration={duration}
      $ease={cssEase}
      $initialOpacity={initialOpacity}
      $animateOpacity={animateOpacity}
      $scale={scale}
    >
      {children}
    </RevealWrapper>
  );
};

/* =========================
   STYLES
   ========================= */

// SECTION WRAPPER
const ContactSection = styled.section`
  height: 100vh;
  min-height: 620px;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #032b58 0%, #002444 50%, #002a55 100%);
  padding: 40px 20px;
  font-family: 'Inter', sans-serif;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.08), transparent 60%);
    opacity: 0.3;
    z-index: 0;
  }
`;

// LAYOUT
const ContentContainer = styled.div`
  max-width: 1200px;
  margin: auto;
  width: 100%;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 40px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

// TEXT BLOCK
const TextContent = styled.div``;

const ContactTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 18px;
  background: linear-gradient(90deg, #ffffff, #cce6ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const TypeCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1.0em;
  background: #023d74ff;
  margin-left: 3px;
  vertical-align: -2px;
  animation: ${blink} 0s steps(1, end)  infinite;
`;

const ContactSubtitle = styled.p`
  font-size: 1.05rem;
  line-height: 1.6;
  opacity: 0.95;
  margin-bottom: 28px;
  max-width: 500px;

  @media (max-width: 900px) {
    margin: 0 auto 28px;
  }
`;

// BUTTON – electric / laser border
const StyledNavLink = styled(NavLink)`
  position: relative;
  display: inline-block;
  border-radius: 999px;
  padding: 14px 42px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #e0faff;
  overflow: hidden;
  z-index: 0;
  transform: translateY(0);
  transition: transform 0.2s ease-out, color 0.2s ease-out;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 999px;
    background: conic-gradient(
      from 0deg,
      #00f5ff,
      #38bdf8,
      #22d3ee,
      #0ea5e9,
      #00f5ff
    );
    background-size: 220% 220%;
    filter: blur(3px);
    opacity: 0.9;
    mix-blend-mode: screen;
    animation: ${electricBorder} 2.6s linear infinite;
    z-index: -2;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 999px;
    background:
      radial-gradient(
        circle at 0% 0%,
        rgba(126, 211, 247, 0.32),
        transparent 60%
      ),
      rgba(6, 12, 30, 0.98);
    box-shadow:
      0 8px 24px rgba(15, 23, 42, 0.9),
      0 0 18px rgba(56, 189, 248, 0.6);
    z-index: -1;
  }

  &:hover {
    transform: translateY(-2px);
    color: #7dd3fc;
  }
`;

// SOCIALS
const SocialMediaContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 25px;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const SocialIcon = styled.a`
  color: white;
  font-size: 1.1rem;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(6px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-3px) scale(1.05);
  }
`;

// PARTNER CARD – subtle electric glow
const PartnerCard = styled.div`
  background: radial-gradient(
      circle at 0% 0%,
      rgba(56, 189, 248, 0.18),
      transparent 55%
    ),
    rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(16px);
  padding: 32px 25px;
  border-radius: 18px;
  text-align: center;
  border: 1px solid rgba(148, 191, 255, 0.45);
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.55),
    0 0 24px rgba(56, 189, 248, 0.4);
`;

const PartnerHeader = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 18px;
  color: #ffffff;
`;

const LogoWrapper = styled.div`
  display: inline-block;
  border-radius: 12px;
  padding: 10px;
  background: white;
  animation: ${pulseGlow} 3s infinite ease-in-out;
`;

const PartnerLogo = styled.img`
  height: 140px;
  object-fit: contain;
`;

const PartnerDescription = styled.p`
  font-size: 0.97rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.55;
  max-width: 380px;
  margin: 16px auto 0;
`;

/* =========================
   MAIN COMPONENT
   ========================= */

const LetsGetInTouch = () => {
  const fullTitle = "Let's Transform Your Digital Future";
  const [typedTitle, setTypedTitle] = useState('');

  // Typewriter effect for the heading
  useEffect(() => {
    let index = 0;
    const speed = 50; // ms per character

    const interval = setInterval(() => {
      index += 1;
      setTypedTitle(fullTitle.slice(0, index));

      if (index >= fullTitle.length) {
        clearInterval(interval);
      }
    }, speed);

    return () => clearInterval(interval);
  }, []);

  return (
    <ContactSection>
      <ContentContainer>
        {/* LEFT: Text + button + socials, sliding in from left */}
        <RevealOnScroll
          distance={90}
          direction="horizontal"
          reverse={true}
          duration={1.9}
          ease="power3.out"
          threshold={0.2}
        >
          <TextContent>
            <ContactTitle aria-label={fullTitle}>
              {typedTitle}
              <TypeCursor />
            </ContactTitle>

            <ContactSubtitle>
              Partner with us to navigate the complexities of digital transformation. 
              Together with Siemens, we deliver innovation, reliability, and performance — 
              building solutions that stand the test of time.
            </ContactSubtitle>

            <StyledNavLink to="/contact#contactusform">
              Schedule Consultation
            </StyledNavLink>

            <SocialMediaContainer>
              <SocialIcon
                href="https://www.linkedin.com/company/nextech-plm-solutions/"
                target="_blank"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={faLinkedinIn} />
              </SocialIcon>

              <SocialIcon
                href="https://wa.me/9970166224"
                target="_blank"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={faWhatsapp} />
              </SocialIcon>

              <SocialIcon
                href="https://www.facebook.com/share/vNf8TQB9UWD12RVY/?mibextid=qi2Omg"
                target="_blank"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={faFacebookF} />
              </SocialIcon>

              <SocialIcon
                href="https://www.instagram.com/nextech_plm_solutions"
                target="_blank"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={faInstagram} />
              </SocialIcon>
            </SocialMediaContainer>
          </TextContent>
        </RevealOnScroll>

        {/* RIGHT: Siemens card, sliding in from right */}
        <RevealOnScroll
          distance={80}
          direction="horizontal"
          reverse={false}
          duration={0.9}
          ease="power3.out"
          threshold={0.2}
          delay={0.1}
        >
          <PartnerCard>
            <PartnerHeader>Strategic Alliance with Siemens</PartnerHeader>
            <LogoWrapper>
              <PartnerLogo src={siemensLogo} alt="Siemens Logo" />
            </LogoWrapper>
            <PartnerDescription>
              We collaborate with Siemens to deliver transformative digital solutions 
              that empower industries worldwide. This alliance combines advanced 
              technology with our expertise to create intelligent, future-ready systems 
              that drive innovation, efficiency, and sustainable growth.
            </PartnerDescription>
          </PartnerCard>
        </RevealOnScroll>
      </ContentContainer>
    </ContactSection>
  );
};

export default LetsGetInTouch;
